<template>
    <div class="bg-white dark:bg-grey-900 lg:bg-transparent">
        <div class="container">
            <Breadcrumb
                v-if="!$route.hash"
                :current-static-route="model?.name"
                :category-slug="model.category_slug"
                :category-name="model.category_name"
            />
            <div
                class="grid grid-cols-12 bg-white lg:rounded-md box-shadow lg:overflow-hidden w-[calc(100%+2.5rem)] -ml-5 lg:ml-0 lg:w-auto"
            >
                <div class="col-span-12 lg:col-span-6 border-r border-black border-opacity-10">
                    <div class="h-full flex items-start justify-center relative">
                        <div class="aspect-w-1 aspect-h-1 w-full max-h-[80vh] relative">
                            <div>
                                <div
                                    id="preview-window"
                                    class="relative aspect-1-1 lg:h-full mx-auto h-full lg:w-full transition max-h-[97vh] overflow-hidden"
                                >
                                    <div
                                        v-if="model.article_number === 'custom-ring-holder-box-1'"
                                        ref="preview-window"
                                        class="absolute w-full h-full left-0 top-0 z-20 flex flex-col justify-center items-center pointer-events-none"
                                    >
                                        <div
                                            class="absolute flex items-center justify-center"
                                            :style="engravedProductsDetails.ringHolderV1.nameStyle"
                                        >
                                            <span class="block font-Cinzel relative engraved-text-to-plastic">{{
                                                engravedProductsDetails.ringHolderV1.name
                                            }}</span>
                                        </div>
                                        <div
                                            class="absolute flex items-center justify-center"
                                            :style="engravedProductsDetails.ringHolderV1.dateStyle"
                                        >
                                            <span class="block font-Cinzel relative engraved-text-to-plastic">{{
                                                engravedProductsDetails.ringHolderV1.date
                                            }}</span>
                                        </div>
                                    </div>
                                    <div
                                        v-else-if="model.article_number === 'custom-ring-holder-box-2'"
                                        ref="preview-window"
                                        class="absolute w-full h-full left-0 top-0 z-20 flex flex-col justify-center items-center"
                                    >
                                        <div
                                            class="absolute flex items-center justify-center"
                                            :style="engravedProductsDetails.ringHolderV2.nameStyle"
                                        >
                                            <span
                                                class="block font-British-Shorthair relative engraved-text-to-plastic engraved-text-to-plastic--light"
                                                >{{ engravedProductsDetails.ringHolderV2.name }}</span
                                            >
                                        </div>
                                    </div>
                                    <div
                                        v-else-if="model.article_number === 'CWB1'"
                                        ref="preview-window"
                                        class="absolute w-full h-full left-0 top-0 z-20 flex flex-col justify-center items-center pointer-events-none"
                                    >
                                        <img
                                            class="absolute w-full h-full object-contain z-30"
                                            :src="require('assets/images/whispers-of-wood/wine_holder_frame.webp')"
                                            :alt="$t('products.product.wine_holder_alt')"
                                        />
                                        <div
                                            class="absolute flex items-center justify-center"
                                            :style="engravedProductsDetails.wineHolder.nameStyle"
                                        >
                                            <span
                                                class="block font-British-Shorthair relative text-white engraved-text-to-wood"
                                                :class="{
                                                    'transition scale-90':
                                                        engravedProductsDetails.wineHolder.name.value.length >= 5 &&
                                                        engravedProductsDetails.wineHolder.name.value.length < 8,
                                                    'scale-[0.7]':
                                                        engravedProductsDetails.wineHolder.name.value.length >= 8,
                                                }"
                                                >{{ engravedProductsDetails.wineHolder.name.value }}</span
                                            >
                                        </div>
                                        <div
                                            class="absolute flex items-center justify-center max-w-[15%]"
                                            :style="engravedProductsDetails.wineHolder.dateStyle"
                                        >
                                            <span
                                                class="block font-Cinzel relative text-white text-center leading-[1.3] break-words w-fill-available engraved-text-to-wood"
                                                >{{ engravedProductsDetails.wineHolder.message.value }}</span
                                            >
                                        </div>
                                    </div>
                                    <div
                                        v-else-if="model.article_number === 'custom-christmas-tree-decoration'"
                                        ref="preview-window"
                                        class="absolute w-full h-full left-0 top-0 z-20 flex flex-col justify-center items-center"
                                    >
                                        <div
                                            class="absolute flex items-center justify-center flex-col font-bold"
                                            :style="engravedProductsDetails.christmasTreeDecoration.nameStyle"
                                        >
                                            <span
                                                class="block min-h-[2.75vh] font-Good-Mood relative engraved-text-to-wood-dark translate-x-[1px] translate-y-[-2px]"
                                                >{{ engravedProductsDetails.christmasTreeDecoration.name }}</span
                                            >
                                            <span
                                                class="block min-h-[2.75vh] font-Good-Mood relative engraved-text-to-wood-dark"
                                                >{{ engravedProductsDetails.christmasTreeDecoration.name2 }}</span
                                            >
                                            <span
                                                class="block min-h-[2.75vh] font-Good-Mood relative engraved-text-to-wood-dark translate-y-[-2px] translate-x-[3px]"
                                                >{{ engravedProductsDetails.christmasTreeDecoration.name3 }}</span
                                            >
                                            <span
                                                class="block min-h-[2.75vh] font-Good-Mood relative engraved-text-to-wood-dark translate-y-[-2px]"
                                                >{{ engravedProductsDetails.christmasTreeDecoration.name4 }}</span
                                            >
                                        </div>
                                        <div
                                            class="absolute flex items-center justify-center max-w-[15%]"
                                            :style="engravedProductsDetails.christmasTreeDecoration.dateStyle"
                                        >
                                            <span
                                                class="block font-British-Shorthair relative engraved-text-to-wood-medium font-black whitespace-nowrap"
                                                >{{ engravedProductsDetails.christmasTreeDecoration.message }}</span
                                            >
                                        </div>
                                    </div>
                                    <div v-else-if="model.article_number === 'rosebox-1'" class="image-container">
                                        <img
                                            :src="
                                                require(`~/assets/images/whispers-of-wood/rose-colors/${
                                                    engravedProductsDetails.roseBox.color.options.find(
                                                        (color) =>
                                                            color.id === engravedProductsDetails.roseBox.color.value
                                                    ).preview
                                                }`)
                                            "
                                            class="absolute w-full h-full top-0 left-0 object-contain z-20 preview-design"
                                        />
                                    </div>
                                    <div v-if="model?.image" class="image-container">
                                        <app-image
                                            :src="
                                                model.article_number === 'custom-ring-holder-box-1' ||
                                                model.article_number === 'custom-ring-holder-box-2' ||
                                                model.article_number === 'CWB1' ||
                                                model.article_number === 'custom-christmas-tree-decoration'
                                                    ? model?.preview
                                                    : model?.image
                                            "
                                            class="absolute w-full h-full top-0 left-0 object-contain z-10 preview-design"
                                            :alt="model?.name || ''"
                                            preset="stage"
                                            eager
                                        />
                                    </div>
                                    <div v-if="model.category_name !== 'Whispers of Wood'" class="image-container">
                                        <img
                                            class="absolute w-full h-full top-0 left-0 object-contain z-1 preview-shadow opacity-100"
                                            :src="
                                                require(`assets/images/dev/star_map_editor/${
                                                    model?.orientation === 'horizontal'
                                                        ? 'shadow--horizontal.webp'
                                                        : 'shadow.webp'
                                                }`)
                                            "
                                            :alt="model?.name"
                                        />
                                    </div>
                                    <div v-if="model.category_name !== 'Whispers of Wood'" class="image-container">
                                        <img
                                            v-if="
                                                (selectedOptions.frame.id &&
                                                    $store.state.globals.editable_options
                                                        .find((option) => option.id === 1)
                                                        .options.find((theme) => theme.id === selectedOptions.frame.id)
                                                        .preview) ||
                                                selectedOptions.frame?.preview
                                            "
                                            :src="
                                                require(`assets/images/dev/frames/${
                                                    $store.state.globals.editable_options
                                                        .find((option) => option.id === 1)
                                                        .options.find((theme) => theme.id === selectedOptions.frame.id)
                                                        .preview || selectedOptions.frame?.preview
                                                }`)
                                            "
                                            class="absolute w-full h-full top-0 left-0 object-contain preview-frame product-image opacity-100 z-1"
                                            :class="model?.orientation === 'horizontal' && 'rotate-90 ml-2 lg:ml-3'"
                                            :alt="model?.name"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div
                                class="measure flex measure--x items-center justify-between w-full"
                                :class="[
                                    isMeasureVisible && 'is-visible',
                                    model?.orientation === 'horizontal' && 'measure--x-horizontal',
                                ]"
                            >
                                <span
                                    :key="
                                        model?.orientation === 'horizontal'
                                            ? $store.state.globals.editable_options[1].options.find(
                                                  (size) => size.id === selectedOptions.size.id
                                              ).height
                                            : $store.state.globals.editable_options[1].options.find(
                                                  (size) => size.id === selectedOptions.size.id
                                              ).width
                                    "
                                    class="font-Inter-Bold text-center text-xs lg:text-sm mb-1 absolute w-full -translate-y-4"
                                    >{{
                                        model?.orientation === 'horizontal'
                                            ? $store.state.globals.editable_options[1].options.find(
                                                  (size) => size.id === selectedOptions.size.id
                                              ).height
                                            : $store.state.globals.editable_options[1].options.find(
                                                  (size) => size.id === selectedOptions.size.id
                                              ).width
                                    }}
                                    cm</span
                                >
                                <i class="ri-arrow-left-s-line -ml-4"></i>
                                <i class="ri-arrow-right-s-line -mr-4"></i>
                            </div>
                            <div
                                class="measure measure--y flex items-center justify-between flex-col"
                                :class="[
                                    isMeasureVisible && 'is-visible',
                                    model?.orientation === 'horizontal' && 'measure--y-horizontal',
                                ]"
                            >
                                <div
                                    class="font-Inter-Bold text-center text-xs lg:text-sm mb-1 absolute h-full -translate-x-5 flex justify-center items-center"
                                >
                                    <span
                                        :key="
                                            model?.orientation === 'horizontal'
                                                ? $store.state.globals.editable_options[1].options.find(
                                                      (size) => size.id === selectedOptions.size.id
                                                  ).width
                                                : $store.state.globals.editable_options[1].options.find(
                                                      (size) => size.id === selectedOptions.size.id
                                                  ).height
                                        "
                                        class="w-max text-xs lg:text-sm font-Inter-Bold -rotate-90"
                                        :class="
                                            model?.orientation === 'horizontal'
                                                ? '-rotate-90 -mr-1 lg:mr-2'
                                                : 'lg:rotate-0 lg:mr-9'
                                        "
                                        >{{
                                            model?.orientation === 'horizontal'
                                                ? $store.state.globals.editable_options[1].options.find(
                                                      (size) => size.id === selectedOptions.size.id
                                                  ).width
                                                : $store.state.globals.editable_options[1].options.find(
                                                      (size) => size.id === selectedOptions.size.id
                                                  ).height
                                        }}
                                        cm</span
                                    >
                                </div>
                                <i class="ri-arrow-up-s-line -mt-5 text-lg"></i>
                                <i class="ri-arrow-down-s-line -mb-5"></i>
                            </div>
                        </div>
                        <button
                            v-if="model.category_name !== 'Whispers of Wood'"
                            class="zoom-button rounded-full w-10 h-10 bg-white bg-no-repeat bg-center absolute right-4 lg:right-8 top-4 lg:top-8 cursor-pointer z-10 transition hidden md:block"
                            @click="openLightbox(model?.preview)"
                        >
                            <i class="ri-zoom-in-line text-[1.5rem]"></i>
                        </button>
                    </div>
                </div>
                <div class="col-span-12 lg:col-span-6 pt-2 lg:pt-9 relative">
                    <div class="flex flex-col justify-between h-full">
                        <div class="px-4 lg:px-14 pb-4">
                            <h1
                                class="font-Inter-Bold text-[1.4rem] lg:text-[1.5rem] 3xl:text-4xl w-11/12 mb-4 leading-8 mt-4 lg:mt-0"
                            >
                                {{ model?.name }}
                            </h1>
                            <div
                                v-if="
                                    false //model?.rating
                                "
                                class="rating-btn-wrapper w-max mb-4 lg:mb-8 transition-all rounded-full"
                            >
                                <Rating
                                    :is-product-card="false"
                                    :rating-value="parseFloat(model?.rating)"
                                    :review-count="400"
                                />
                            </div>
                            <div class="flex flex-col gap-y-2 mb-2">
                                <div class="flex items-center text-sm 2xl:text-base 3xl:text-lg">
                                    <strong>{{ $t('products.product.category') }}</strong
                                    ><nuxt-link
                                        :to="
                                            localePath({
                                                name: 'products-category',
                                                params: { category: model?.category_slug },
                                            })
                                        "
                                        class="ml-3 hover:underline"
                                        >{{ model?.category_name }}</nuxt-link
                                    >
                                </div>
                            </div>
                            <div v-if="model?.expected_delivery_time" class="flex flex-col gap-y-2 mb-4 lg:mb-6">
                                <div class="flex items-center text-sm 2xl:text-base 3xl:text-lg">
                                    <strong>{{ $t('products.product.expected_delivery') }}</strong
                                    ><span class="ml-3">{{ model?.expected_delivery_time || $t('products.product.default_delivery_time') }}</span>
                                </div>
                            </div>
                            <div
                                v-if="model?.description"
                                class="product-description leading-5 2xl:text-base"
                                :class="model.category_name !== 'Whis' ? 'mb-6' : 'mb-4'"
                                v-html="model?.description"
                            ></div>
                            <div
                                v-if="model.article_number === 'custom-ring-holder-box-1'"
                                class="flex items-center mb-4"
                            >
                                <i class="ri-information-line mr-1"></i>
                                <span class="text-sm font-Inter-Semibold text-black"
                                    >{{ $t('products.product.text_length_hint') }}</span
                                >
                            </div>
                            <ValidationObserver
                                v-if="model.article_number === 'CWB1'"
                                class="grid grid-cols-2 gap-x-4 gap-y-3"
                            >
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.name') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.wineHolder.name.value"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_names')"
                                            :error="errors.size"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.wineHolder.name.value =
                                                    engravedProductsDetails.wineHolder.name.value.slice(0, 9)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.message') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.wineHolder.message.value"
                                            class="w-full"
                                            :placeholder="$t('products.product.choose_date')"
                                            :error="errors.size"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.wineHolder.message.value =
                                                    engravedProductsDetails.wineHolder.message.value.slice(0, 178)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                            </ValidationObserver>
                            <ValidationObserver
                                v-else-if="model.article_number === 'custom-ring-holder-box-1'"
                                class="grid grid-cols-2 gap-x-4 gap-y-3"
                            >
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.names') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.ringHolderV1.name"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_names')"
                                            :error="errors.size"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.ringHolderV1.name =
                                                    engravedProductsDetails.ringHolderV1.name.slice(0, 23)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.date') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.ringHolderV1.date"
                                            class="w-full"
                                            :placeholder="$t('products.product.choose_date')"
                                            :error="errors.size"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.ringHolderV1.date =
                                                    engravedProductsDetails.ringHolderV1.date.slice(0, 28)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                            </ValidationObserver>
                            <ValidationObserver
                                v-else-if="model.article_number === 'custom-ring-holder-box-2'"
                                class="grid grid-cols-2 gap-x-4 gap-y-3"
                            >
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.names') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.ringHolderV2.name"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_names')"
                                            :error="errors.size"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.ringHolderV2.name =
                                                    engravedProductsDetails.ringHolderV2.name.slice(0, 17)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <div>
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.comment') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.ringHolderV2.comment"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_comment')"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.ringHolderV1.date =
                                                    engravedProductsDetails.ringHolderV1.date.slice(0, 28)
                                            "
                                        />
                                    </div>
                                </div>
                            </ValidationObserver>
                            <div
                                v-else-if="model.article_number === 'custom-christmas-tree-decoration'"
                                class="grid grid-cols-2 gap-x-4 gap-y-3"
                            >
                                <div class="col-span-2 lg:col-span-1">
                                    <div class="w-full">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.name') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.christmasTreeDecoration.name"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_name')"
                                            @input="
                                                engravedProductsDetails.christmasTreeDecoration.name =
                                                    engravedProductsDetails.christmasTreeDecoration.name.slice(0, 6)
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <div class="w-full">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.name_numbered', { n: 2 }) }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.christmasTreeDecoration.name2"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_name')"
                                            @input="
                                                engravedProductsDetails.christmasTreeDecoration.name2 =
                                                    engravedProductsDetails.christmasTreeDecoration.name2.slice(0, 10)
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <div class="w-full">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.name_numbered', { n: 3 }) }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.christmasTreeDecoration.name3"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_name')"
                                            @input="
                                                engravedProductsDetails.christmasTreeDecoration.name3 =
                                                    engravedProductsDetails.christmasTreeDecoration.name3.slice(0, 16)
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <div>
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.name_numbered', { n: 4 }) }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.christmasTreeDecoration.name4"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_name')"
                                            @input="
                                                engravedProductsDetails.christmasTreeDecoration.name4 =
                                                    engravedProductsDetails.christmasTreeDecoration.name4.slice(0, 19)
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="col-span-2">
                                    <div class="w-full">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.inscription') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.christmasTreeDecoration.message"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_name')"
                                            @input="
                                                engravedProductsDetails.christmasTreeDecoration.message =
                                                    engravedProductsDetails.christmasTreeDecoration.message.slice(0, 17)
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                            <ValidationObserver
                                v-else-if="model.article_number === 'rosebox-1'"
                                class="grid grid-cols-2 gap-x-4 gap-y-3"
                            >
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.colour') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.roseBox.color.value"
                                            class="w-full"
                                            is-dropdown
                                            :options="roseColorOptions"
                                            :placeholder="$t('products.product.enter_names')"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.roseBox.name =
                                                    engravedProductsDetails.roseBox.name.slice(0, 17)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{ $t('products.product.name') }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.roseBox.name"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_names')"
                                            :is-error-visible="isErrorVisible"
                                            @input="
                                                engravedProductsDetails.roseBox.name =
                                                    engravedProductsDetails.roseBox.name.slice(0, 17)
                                            "
                                        />
                                    </ValidationProvider>
                                </div>
                            </ValidationObserver>
                            <ValidationObserver
                                v-else-if="model.category_name !== 'Whispers of Wood'"
                                tag="div"
                                class="grid grid-cols-2 gap-4"
                            >
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2 mb-1 lg:mb-3"
                                        >
                                            {{ $t('products.product.size') }}
                                        </span>
                                        <Input
                                            v-model="selectedOptions.size.id"
                                            is-dropdown
                                            :options="
                                                $store.state.globals.editable_options.find((option) => option.id === 2)
                                                    .options
                                            "
                                            class="w-full"
                                            :placeholder="$t('products.product.choose_size')"
                                            :error="errors.size"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>
                                </div>
                                <div class="col-span-2 lg:col-span-1">
                                    <ValidationProvider v-slot="{ errors }" vid="first_name" tag="div">
                                        <div class="flex items-center justify-between mb-3">
                                            <span
                                                class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                            >
                                                {{ $t('products.product.frame') }}
                                            </span>
                                            <transition name="slide">
                                                <div v-if="selectedOptions.frame.id">
                                                    <div
                                                        class="rounded-full flex items-center justify-center cursor-pointer p-[0.9rem] pl-3 bg-black text-white transition opacity-80 hover:opacity-100 h-4"
                                                        @click="clearOption('frame')"
                                                    >
                                                        <i class="ri-close-line text-base mr-1"></i>
                                                        <span
                                                            class="font-Inter-Semibold text-xs block whitespace-nowrap"
                                                            >{{ $t('products.frame.none') }}</span
                                                        >
                                                    </div>
                                                </div>
                                            </transition>
                                        </div>
                                        <Input
                                            v-model="selectedOptions.frame.id"
                                            :options="frameOptions"
                                            is-dropdown
                                            class="w-full"
                                            :placeholder="$t('products.product.choose_frame')"
                                            :required="false"
                                            :error="errors.frame"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>
                                </div>
                                <div class="col-span-2 lg:col-span-2">
                                    <ValidationProvider vid="has-digitial-format" tag="div" class="w-ful mt-2">
                                        <Input
                                            v-model="selectedOptions.hasDigital.active"
                                            is-checkbox
                                            :label="$t('products.product.digital_copy_label')"
                                            :required="false"
                                            class="font-Inter-Bold"
                                        />
                                    </ValidationProvider>
                                </div>
                            </ValidationObserver>
                            <div v-else class="grid grid-cols-2 gap-x-4 gap-y-3">
                                <div class="col-span-2">
                                    <div>
                                        <span
                                            class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label py-2"
                                        >
                                            {{
                                                model.article_number === 'rosebox2'
                                                    ? $t('products.product.rose_name_and_colour')
                                                    : $t('products.product.comment')
                                            }}
                                        </span>
                                        <Input
                                            v-model="engravedProductsDetails.message"
                                            class="w-full"
                                            :placeholder="$t('products.product.enter_names')"
                                            is-textarea
                                            :rows="2"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="px-4 lg:px-14 pt-2 pb-2 lg:pt-[2.1rem] lg:pb-7 flex justify-between items-center border-t fixed bg-white bottom-0 w-full z-20 lg:w-auto sm:relative border-black border-opacity-10"
                        >
                            <div class="flex items-baseline flex-col">
                                <div class="flex pr-4 lg:pr-9 items-baseline gap-x-2 lg:gap-x-4">
                                    <transition name="fade-in-value" mode="out-in">
                                        <span
                                            v-if="model?.price"
                                            :key="
                                                $formatCurrency(
                                                    parseInt(
                                                        model.category_name === 'Whispers of Wood' ? model.price : price
                                                    )
                                                )
                                            "
                                            class="block text-xl lg:text-2xl font-Inter-Bold mb-[0.4rem] lg:mb-2 leading-none"
                                            >{{
                                                $formatCurrency(
                                                    parseInt(
                                                        model.category_name === 'Whispers of Wood' ? model.price : price
                                                    )
                                                )
                                            }}</span
                                        >
                                    </transition>
                                </div>
                                <div class="flex lg:hidden pr-9 items-baseline gap-x-2">
                                    <span
                                        class="block text-md opacity-60 font-Inter-Regular lg:mb-1 text-xs lg:text-base"
                                        >{{ $t('products.gross_price', { price: $formatCurrency(parseInt(price)) }) }}
                                    </span>
                                </div>
                                <div class="mt-1 lg:mb-1 text-md hidden lg:flex">
                                    <span
                                        v-if="model?.stock > 0"
                                        class="font-Inter-Bold text-green text-sm lg:text-base"
                                        >{{ $t('products.product.in_stock') }}</span
                                    >
                                    <span v-else class="font-Inter-Bold text-orange-200 text-sm lg:text-base"
                                        >{{ $t('products.product.out_of_stock') }}</span
                                    >
                                </div>
                            </div>
                            <div
                                class="flex items-center"
                                :class="model?.stock < 1 && 'opacity-40 pointer-events-none'"
                            >
                                <Input
                                    v-model="quantity"
                                    :min="1"
                                    is-number-incrementer
                                    :placeholder="$t('products.product.quantity')"
                                    :required="false"
                                    class="w-[6rem] h-[4rem] rounded-[2px] mr-6 number-input text-center quantity flex items-center"
                                />

                                <div
                                    @click="
                                        addToCart({
                                            id: model.id,
                                            price: { from: price * 2 },
                                            brutto_price: { from: price },
                                            image: previewImage,
                                            slug: localePath({
                                                name: 'products-category-product',
                                                params: {
                                                    category: model.category_slug,
                                                    product: model.slug,
                                                },
                                                hash: `#${randomId}`,
                                            }),
                                            category: model.category,
                                            related_categories: model?.related_categories,
                                            isCustomized: false,
                                            name: model.name,
                                            options: mutatedOptions,
                                        })
                                    "
                                >
                                    <div
                                        class="transition cart-btn font-Inter-Semibold text-center rounded-full bg-yellow-100 hover:bg-yellow-200 p-0 justify-center text-black flex items-center text-lg opacity-100 cursor-pointer h-14 lg:h-16 w-14 lg:w-16"
                                        :class="isPreviewCapturing && 'pointer-events-none opacity-60'"
                                    >
                                        <i
                                            class="text-2xl"
                                            :class="
                                                isLoading
                                                    ? 'ri-loader-4-line animate-spin block transition leading-none pointer-events-none'
                                                    : 'ri-shopping-cart-line'
                                            "
                                        ></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        :class="liked_products.find((item) => item.id === model?.id) && 'pointer-events-none'"
                        class="absolute top-8 md:top-6 right-6 w-7 h-7 flex items-center rounded-full transition-all min-w-0 bg-no-repeat bg-center bg-contain bg-right w-full cursor-pointer bg-white lg:bg-transparent"
                        @click="$auth.loggedIn ? addToWishlist(model) : $store.commit('globals/toggleLoginModal', true)"
                    >
                        <i
                            class="text-2xl text-center text-black transition hover:opacity-100"
                            :class="
                                isOnWishlist
                                    ? 'ri-heart-fill pointer-events-none text-red-100 opacity-100 bounce'
                                    : 'ri-heart-fill opacity-20 text-black'
                            "
                        ></i>
                    </div>
                </div>
            </div>
            <div class="grid grid-cols-12 mt-8 mb-8 lg:mb-0 lg:gap-x-6">
                <div
                    v-if="model?.gallery && model?.gallery.length"
                    class="col-span-12 bg-white rounded-xl lg:px-9 lg:pt-8 lg:pb-14 mb-8"
                >
                    <GallerySection :images="model?.gallery" />
                </div>
                <div
                    v-if="testimonials"
                    class="col-span-12 bg-white rounded-xl lg:px-9 lg:pt-8 lg:pb-8 mb-8 box-shadow-sm"
                >
                    <h3
                        class="font-Inter-Bold pb-3 w-full block border-b border-dashed border-black border-opacity-20 text-xl mb-7"
                    >
                        {{ $t('products.customer_reviews') }}
                    </h3>

                    <div class="relative">
                        <div id="reviews-product-page" class="swiper w-full lg:min-h-[10rem] lg:flex pt-2">
                            <div class="swiper-wrapper items-center">
                                <div
                                    v-for="review in testimonials"
                                    :key="`product-review-${review.id}`"
                                    class="swiper-slide flex lg:flex-row flex-col justify-center lg:justify-start items-center px-8 lg:px-14"
                                >
                                    <div class="flex flex-col w-full" :class="review.image ? 'lg:w-9/12' : 'w-full'">
                                        <span class="block font-Inter-Semibold text-xl mb-3">{{ review.name }}</span>
                                        <Rating :is-product-card="false" :rating-value="5" class="mb-4 italic" />
                                        <p
                                            class="block font-Inter-Regular lg:text-lg w-full lg:w-9/12 text-left lg:pr-12 leading-[1.45]"
                                        >
                                            {{ review.text }}
                                        </p>
                                    </div>
                                    <div v-if="review.image" class="lg:w-3/12 mt-4 lg:mt-0 relative group">
                                        <div
                                            class="aspect-w-4 aspect-h-3 rounded-md overflow-hidden border border-black border-opacity-10"
                                        >
                                            <app-image class="object-cover" :src="review.image || ''" :alt="review.name || ''" preset="photo" />
                                        </div>
                                        <button
                                            class="zoom-button rounded-full w-8 h-8 bg-white bg-no-repeat bg-center absolute right-4 top-4 cursor-pointer z-30 transition opacity-0 group-hover:opacity-100"
                                            @click="openLightbox(review.image)"
                                        >
                                            <i class="ri-zoom-in-line"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="swiper-button-prev lg:-left-2"></div>
                            <div class="swiper-button-next lg:-right-2"></div>
                        </div>
                        <div class="mt-6 lg:mt-16 relative hidden">
                            <div class="swiper-pagination"></div>
                        </div>
                    </div>
                </div>

                <div class="col-span-12 bg-white rounded-xl lg:px-9 lg:pt-8 lg:pb-14 mt-8 lg:mt-0">
                    <h3
                        class="font-Inter-Bold pb-3 w-full block border-b border-dashed border-black border-opacity-20 text-xl mb-7"
                    >
                        {{ $t('products.product.shipping_methods') }}
                    </h3>
                    <div class="flex flex-row flex-wrap gap-y-7 justify-between">
                        <div v-for="method in shipping" :key="`${method.id}`" class="flex items-start gap-x-4">
                            <app-image
                                :src="method?.image || ''"
                                alt="advantage-icon"
                                class="h-[35px] w-[35px] max-h-[35px] max-w-[35px] min-h-[35px] min-w-[35px]"
                                preset="thumb"
                            />
                            <div class="flex flex-col">
                                <span class="font-Inter-Bold leading-none mb-2 block">{{ method?.title }}</span>
                                <p class="font-Inter-Regular text-sm block">
                                    <span class="font-Inter-Semibold">{{ $t('products.product.price') }}</span>
                                    {{ $formatCurrency(method?.cost) }}
                                </p>
                                <p class="font-Inter-Regular text-sm block mt-1">
                                    <span class="font-Inter-Semibold">{{ $t('products.product.available_from') }}</span>
                                    {{
                                        $dayjs(
                                            new Date().setDate(
                                                new Date().getDate() + parseInt(method.cost_three_title || 2)
                                            )
                                        ).format('YYYY. MMMM D.')
                                    }}
                                </p>
                            </div>
                        </div>
                        <div class="flex items-start gap-x-4">
                            <img
                                :src="require('~/assets/images/icons/advantages-icon.svg')"
                                alt="advantage-icon"
                                class="h-[35px] w-[35px] max-h-[35px] max-w-[35px] min-h-[35px] min-w-[35px]"
                            />
                            <div class="flex flex-col">
                                <span class="font-Inter-Bold leading-none mb-2">{{ $t('products.product.payment') }}</span>
                                <ul class="font-Inter-Regular text-sm adavantage-list">
                                    <li class="mb-1">{{ $t('products.product.secure_card_payment') }}</li>
                                    <li>{{ $t('products.product.via_barion') }}</li>
                                </ul>
                            </div>
                        </div>
                        <div class="flex items-start gap-x-4">
                            <img :src="require('~/assets/images/icons/info-fill-icon.svg')" alt="advantage-icon" />
                            <div class="flex flex-col">
                                <span class="font-Inter-Bold leading-none mb-2">{{ $t('products.product.useful_info') }}</span>
                                <ul class="font-Inter-Regular text-sm adavantage-list">
                                    <li class="mb-1">{{ $t('products.product.mind_delivery_time') }}</li>
                                    <li>{{ $t('products.product.order_in_time') }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="model?.connected_products && model?.connected_products.length">
                <SectionIntro :title="$t('products.product.related_products')" class="mt-14" md />
                <div class="grid grid-cols-2 lg:grid-cols-10 gap-y-4 gap-4">
                    <div
                        v-for="product in model?.connected_products.slice(0, 5)"
                        :key="`related-product-${product.id}`"
                        class="h-full col-span-1 md:col-span-6 lg:col-span-2"
                        :class="model?.connected_products > 1 ? 'lg:col-span-3' : 'col-span-12'"
                    >
                        <ProductCard
                            :title="product.name"
                            :rating="5"
                            :has-btn-text="false"
                            :product="product"
                            :slug="
                                localePath({
                                    name: 'products-category-product',
                                    params: {
                                        category: product?.category_slug,
                                        product: product?.slug,
                                    },
                                })
                            "
                            :brutto-price="product.price"
                            :is-favorite="product?.isFavorite"
                            :price="product.price"
                            :discounted-price="product?.discounted_price"
                            :md="$device.isMobile"
                            :image="product.preview"
                            :is-semi-rounded="product?.has_banner"
                        />
                    </div>
                </div>
                <BannerZone
                    v-if="banner"
                    :slug="banner.link"
                    :title="banner.title_hu"
                    :mobile-image="banner.mobile_image"
                    :desktop-image="banner.image"
                    class="lg:mt-12 xl:mb-0"
                />
            </div>
        </div>
        <BaseModal :close="closeLightbox" :is-open="isLightboxOpen">
            <div class="bg-white overflow-hidden rounded-md lg:w-[60vw] max-h-[89vh] lg:h-auto">
                <img
                    v-if="currentImage"
                    :src="currentImage"
                    :alt="model.name"
                    class="mx-auto h-auto lg:h-full max-h-[89vh]"
                    :class="model.category_name === 'Whispers of Wood' ? 'w-full object-cover' : 'w-auto'"
                />
            </div>
        </BaseModal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Breadcrumb from '@/components/UI/Links/Breadcrumb.vue';
import ProductCard from '@/components/Cards/ProductCard/ProductCard.vue';
import BannerZone from '@/components/BannerZone.vue';
import Rating from '@/components/UI/Rating.vue';
import BaseModal from '@/components/UI/BaseModal.vue';
import GallerySection from '@/components/Sections/Gallery.vue';

export default {
    name: 'Product',
    components: {
        Breadcrumb,
        ProductCard,
        BannerZone,
        Rating,
        BaseModal,
        GallerySection,
    },
    async asyncData({ $axios, app, params, error }) {
        let model = null;
        let price = 12000;
        let testimonials = [];
        let shipping = [];
        let banner = [];

        try {
            await Promise.allSettled([
                $axios.$get(`/product/${params.product}`),
                $axios.$get(`/testimonials`),
                $axios.$get(`/shipping`),
                $axios.$get(`/banners`),
            ])
                .then(([productRes, testimonalsRes, shippingRes, bannerRes]) => {
                    if (productRes.status === 'fulfilled' && productRes.value.result === 'success') {
                        model = productRes.value.data;
                        price = parseInt(model.price);
                    }
                    if (testimonalsRes.status === 'fulfilled' && testimonalsRes.value.result === 'success') {
                        testimonials = testimonalsRes.value.data;
                    }
                    if (shippingRes.status === 'fulfilled' && shippingRes.value.result === 'success') {
                        shipping = shippingRes.value.data;
                    }
                    if (bannerRes.status === 'fulfilled' && bannerRes.value.result === 'success') {
                        banner = bannerRes.value.data.find((banner) => banner.name === 'product-page');
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        if (!model) {
            return error({
                statusCode: 404,
                message: app.i18n.t('products.product.not_available'),
            });
        }

        return {
            model,
            price,
            testimonials,
            shipping,
            banner,
        };
    },
    data() {
        return {
            quantity: 1,
            currentImage: null,
            randomId: null,
            previewImage: null,
            isLightboxOpen: false,
            isProductExist: false,
            shippingInformation: null,
            isCustomerReviewModalOpen: false,
            isPreviewCapturing: false,
            isMeasureVisible: false,
            isProductImageLoading: true,
            isLoading: false,
            productDetails: {
                sizes: [
                    { id: 1, name: '30*40cm', price: 3500 },
                    { id: 2, name: '40*50cm', price: 4250 },
                    { id: 3, name: '50*70cm', price: 5000 },
                    { id: 4, name: '60*90cm', price: 6500 },
                ],
                frames: [
                    { id: 1, name: 'Fekete', price: 0 },
                    { id: 2, name: 'Fehér', price: 0 },
                    { id: 3, name: 'Nem kérek keretet', price: -3000 },
                ],
            },
            selectedOptions: {
                size: {
                    id: 1,
                    name: '10*20cm (A5)',
                    price: 0,
                },
                frame: {
                    id: 1,
                    name: 'Fekete',
                    price: 0,
                },
                hasDigital: {
                    active: false,
                    price: 500,
                },
            },
            engravedProductsDetails: {
                ringHolderV1: {
                    name: this.$t('products.product.default_names'),
                    date: '2024.07.11.',
                    su: {},
                    dateStyle: {},
                    price: 0,
                },
                ringHolderV2: {
                    name: 'Gréti és Márk',
                    nameStyle: {},
                    comment: '',
                    price: 0,
                },
                wineHolder: {
                    name: { id: 1, value: this.$t('products.product.default_wine_name'), price: 0 },
                    message: {
                        id: 2,
                        value: this.$t('products.product.default_wine_message'),
                        price: 0,
                    },
                    nameStyle: {},
                    dateStyle: {},
                },
                christmasTreeDecoration: {
                    name: 'Niki',
                    name2: 'Zoli',
                    name3: 'Kriszta',
                    name4: 'Fruzsi',
                    message: this.$t('products.product.default_xmas_message'),
                    nameStyle: {},
                    dateStyle: {},
                    price: 0,
                },
                roseBox: {
                    color: {
                        value: 4,
                        options: [
                            { id: 1, name: 'Lila', preview: 'purple.webp', price: 0 },
                            { id: 2, name: 'Kék', preview: 'blue.webp', price: 0 },
                            { id: 3, name: 'Türkíz', preview: 'turquoise.webp', price: 0 },
                            { id: 4, name: 'Piros', preview: 'red.webp', price: 0 },
                        ],
                    },
                    name: '',
                    comment: '',
                    nameStyle: {},
                },
                message: '',
            },
            isHorizontal: false,
            selectedSize: 2,
            message: null,
            mutatedOptions: null,
            isErrorVisible: false,
            lastSelectedSize: null,
            isOnWishlist: false,
        };
    },
    head() {
        const meta = [
            {
                hid: 'og:title',
                property: 'og:title',
                content: this.model.meta_title || this.model?.name,
            },
            {
                hid: 'title',
                name: 'title',
                content: this.model.meta_title || this.model?.name,
            },
            {
                hid: 'og:description',
                property: 'og:description',
                content: this.model.meta_description || this.model.short_description,
            },
            {
                hid: 'description',
                name: 'description',
                content: this.model.meta_description || this.model.short_description,
            },
        ];

        if (this.model?.preview) {
            meta.push({
                hid: 'og:image',
                property: 'og:image',
                content: this.model?.ogimage || this.model?.preview,
            });
            meta.push({
                hid: 'og:image:url',
                property: 'og:image:url',
                content: this.model?.ogimage || this.model?.preview,
            });
        }

        return {
            title: this.model.meta_title || this.model?.name,
            meta,
            noscript: [
                {
                    hid: 'barionPixel',
                    innerHTML: `
                    <img height="1" width="1" style="display:none" alt="Barion Pixel"
                    src="https://pixel.barion.com/a.gif?ba_pixel_id='BP-vjKR5HKA1G-FE'&ev=contentView&noscript=1">
                `,
                },
            ],
            __dangerouslyDisableSanitizersByTagID: {
                barionPixel: ['innerHTML'],
            },
        };
    },
    computed: {
        ...mapState(['liked_products']),
        frameOptions() {
            return this.$store.state.globals.editable_options
                .find((option) => option.id === 1)
                .options.map((option) => ({ ...option, name: this.$t(option.name) }));
        },
        roseColorOptions() {
            // names stay Hungarian in data because they are sent to the API with the cart extras
            return this.engravedProductsDetails.roseBox.color.options.map((option) => ({
                ...option,
                name: this.$t(`products.rose_color.${option.preview.split('.')[0]}`),
            }));
        },
        setEngravedProductDetails() {
            const excludeStyles = (details) =>
                Object.fromEntries(
                    Object.entries(details).filter(([key]) => !['nameStyle', 'dateStyle'].includes(key))
                );

            if (this.model.article_number === 'custom-ring-holder-box-1') {
                return excludeStyles(this.engravedProductsDetails.ringHolderV1);
            } else if (this.model.article_number === 'custom-ring-holder-box-2') {
                return excludeStyles(this.engravedProductsDetails.ringHolderV2);
            } else if (this.model.article_number === 'CWB1') {
                return excludeStyles(this.engravedProductsDetails.wineHolder);
            } else if (this.model.article_number === 'custom-christmas-tree-decoration') {
                return excludeStyles(this.engravedProductsDetails.christmasTreeDecoration);
            } else if (this.model.article_number === 'rosebox-1') {
                return excludeStyles(this.engravedProductsDetails.roseBox);
            } else {
                return { comment: this.engravedProductsDetails.message };
            }
        },
    },
    watch: {
        selectedOptions: {
            handler: 'handleSelectedOptionsChange',
            deep: true,
        },
        quantity: {
            handler: 'handleQuantityChange',
            deep: true,
        },
    },
    mounted() {
        this.reviewCarousel = new this.$swiper(`#reviews-product-page`, {
            slidesPerView: 1,
            allowTouchMove: true,
            spaceBetween: 10,
            loop: false,
            autoplay: {
                delay: 5000,
            },
            slideToClickedSlide: true,
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
        });

        const contentViewProperties = {
            contentType: 'Product',
            currency: 'HUF',
            id: this.model.id,
            name: this.model.name,
            quantity: this.quantity,
            unit: 'pcs.',
            unitPrice: this.model.price,
            brand: 'Kartapolis',
            category: this.model.category_name,
            imageUrl: this.model.preview,
            list: 'ProductPage',
        };

        this.checkOptions(this.selectedOptions);

        if (this.model.isFavorite) {
            this.isOnWishlist = true;
        }
        if (
            this.$route.hash &&
            this.$store.state.data.customizedProducts.find(
                (options) => options.id === parseInt(this.$route.hash.substring(1))
            )
        ) {
            this.isProductExist = true;
            this.selectedOptions = this.$store.state.data.customizedProducts.find(
                (options) => options.id === parseInt(this.$route.hash.substring(1))
            );
        }
        if (
            this.model.article_number === 'custom-ring-holder-box-1' ||
            this.model.article_number === 'custom-ring-holder-box-2' ||
            this.model.article_number === 'custom-christmas-tree-decoration' ||
            this.model.article_number === 'CWB1'
        ) {
            this.updateFontSize();
            window.addEventListener('resize', this.updateFontSize);
        }

        if (typeof window.bp === 'function') {
            window.bp('track', 'contentView', contentViewProperties);
        }
    },
    beforeDestroy() {
        if (this.model?.customer_reviews) {
            this.reviewCarousel.destroy();
        }
        if (
            this.model.article_number === 'custom-ring-holder-box-1' ||
            this.model.article_number === 'custom-ring-holder-box-2' ||
            this.model.article_number === 'CWB1'
        ) {
            window.removeEventListener('resize', this.updateFontSize);
        }
    },
    methods: {
        async addToCart(product) {
            this.isLoading = true;
            try {
                await this.$axios
                    .$post('/cart-item-add', {
                        product_id: product.id,
                        quantity: this.quantity,
                        session_id: this.$cookies.get('hsession'),
                        image: this.previewImage,
                        extras:
                            this.model.category_name === 'Whispers of Wood'
                                ? this.setEngravedProductDetails
                                : this.selectedOptions,
                        update: 0,
                    })
                    .then((res) => {
                        if (res.success === true) {
                            this.$axios.get(`/cart/${this.$cookies.get('hsession')}`).then((res) => {
                                this.$store.commit('checkout/setCartItemsCount', res.data.products.length);
                            });
                        }
                        this.isLoading = false;
                    });
            } catch (err) {
                console.log(err);
            }

            this.$store.commit('data/storeCustomizedProductDetails', {
                ...this.$cloneDeep(this.selectedOptions),
                id: this.randomId,
            });

            if (this.$store.state.globals.notifications.find((item) => item.productId === product.id)) return;
            this.$store.commit('globals/setNotifications', {
                title: this.$t('products.notifications.added_to_cart_title'),
                subtitle: this.$t('products.notifications.added_to_cart_subtitle'),
                emoji: 'thumbs_up_emoji.png',
                productId: product.id,
            });
        },
        addToWishlist(product) {
            if (product.isFavorite || this.isOnWishlist) {
                try {
                    this.$axios.$post(`/remove-favorite/${product.id}`).then((res) => {
                        if (res.success === true) {
                            this.isOnWishlist = false;
                        }
                    });
                } catch (err) {
                    console.log(err);
                }
            } else {
                try {
                    this.$axios.$post(`/add-favorite/${product.id}`).then((res) => {
                        if (res.success === true) {
                            this.isOnWishlist = true;
                        }
                    });
                } catch (err) {
                    console.log(err);
                }
            }
        },
        openLightbox(image) {
            if (image) {
                this.currentImage = image;
            } else {
                this.currentImage = this.model.image;
            }
            this.isLightboxOpen = true;
        },
        updateFontSize() {
            this.$nextTick(() => {
                const containerWidth = this.$refs['preview-window'].clientWidth;
                const calculateDimensions = (
                    width,
                    nameFont,
                    dateFont,
                    nameX,
                    nameY,
                    dateX,
                    dateY,
                    nameRotation,
                    dateRotation,
                    nameSkewX,
                    nameSkewY,
                    dateSkewX,
                    dateSkewY,
                    nameScaleX,
                    nameScaleY,
                    dateScaleX,
                    dateScaleY
                ) => ({
                    nameFontSize: `${width / nameFont}px`,
                    dateFontSize: `${width / dateFont}px`,
                    nameTranslateX: width * nameX,
                    nameTranslateY: width * nameY,
                    dateTranslateX: width * dateX,
                    dateTranslateY: width * dateY,
                    nameRotation,
                    dateRotation,
                    nameSkewX,
                    nameSkewY,
                    dateSkewX,
                    dateSkewY,
                    nameScaleX,
                    nameScaleY,
                    dateScaleX,
                    dateScaleY,
                });
                const ringHolderDimensions = calculateDimensions(
                    containerWidth,
                    32,
                    37,
                    -0.075,
                    0.018,
                    -0.055,
                    0.067,
                    -23.8,
                    -23.8,
                    0,
                    0,
                    0,
                    0,
                    1,
                    1,
                    1,
                    1
                );
                const ringHolderV2Dimensions = calculateDimensions(
                    containerWidth,
                    17,
                    37,
                    0.11,
                    0.095,
                    -0.055,
                    0.067,
                    -27,
                    -27,
                    0,
                    -1,
                    0,
                    0,
                    0.95,
                    1,
                    1,
                    1
                );
                const wineHolderDimensions = calculateDimensions(
                    containerWidth,
                    16,
                    62,
                    -0.025,
                    -0.29,
                    -0.034,
                    0.263,
                    1,
                    0,
                    0,
                    0,
                    0,
                    1,
                    0.95,
                    1,
                    1,
                    1.06
                );
                const christmasTreeDecorationDimensions = calculateDimensions(
                    containerWidth,
                    25,
                    19,
                    0.01,
                    0.04,
                    -0.02,
                    0.253,
                    14,
                    12.5,
                    4,
                    0,
                    0,
                    -2,
                    0.95,
                    1,
                    1,
                    1.06
                );

                // Apply styles
                const applyStyles = (holder, dimensions) => {
                    this.engravedProductsDetails[holder].nameStyle = {
                        fontSize: dimensions.nameFontSize,
                        transform: `translate(${dimensions.nameTranslateX}px, ${dimensions.nameTranslateY}px) rotate(${dimensions.nameRotation}deg) skew(${dimensions.nameSkewX}deg, ${dimensions.nameSkewY}deg) scale(${dimensions.nameScaleX}, ${dimensions.nameScaleY})`,
                    };
                    this.engravedProductsDetails[holder].dateStyle = {
                        fontSize: dimensions.dateFontSize,
                        transform: `translate(${dimensions.dateTranslateX}px, ${dimensions.dateTranslateY}px) rotate(${dimensions.dateRotation}deg) skew(${dimensions.dateSkewX}deg, ${dimensions.dateSkewY}deg) scale(${dimensions.dateScaleX}, ${dimensions.dateScaleX})`,
                    };
                };

                // Apply dimensions to each specific holder
                applyStyles('ringHolderV1', ringHolderDimensions);
                applyStyles('ringHolderV2', ringHolderV2Dimensions);
                applyStyles('wineHolder', wineHolderDimensions);
                applyStyles('christmasTreeDecoration', christmasTreeDecorationDimensions);
            });
        },
        closeLightbox() {
            this.currentImage = null;
            this.isLightboxOpen = false;
        },

        createRandomId() {
            const min = 1;
            const max = 1000;
            this.randomId = Math.floor(Math.random() * (max - min + 1)) + min;
        },
        checkOptions(currentOptions) {
            if (!this.isProductExist) {
                this.createRandomId();
            } else {
                this.randomId = parseInt(this.$route.hash.substring(1));
            }

            this.selectedOptions.frame.name = this.productDetails.frames.find(
                (frame) => frame.id === currentOptions.frame.id
            )?.name;

            this.selectedOptions.frame.price = this.productDetails.frames.find(
                (frame) => frame.id === currentOptions.frame.id
            )?.price;

            this.selectedOptions.size.name = this.productDetails.sizes.find(
                (size) => size.id === currentOptions.size.id
            )?.name;

            this.selectedOptions.size.price = this.productDetails.sizes.find(
                (size) => size.id === currentOptions.size.id
            )?.price;

            if (this.selectedOptions.hasDigital.active) {
                this.selectedOptions.hasDigital.price = 500;
            } else {
                this.selectedOptions.hasDigital.price = 0;
            }

            this.mutatedOptions = { ...this.selectedOptions };
            this.price = this.model?.price;

            Object.values(this.selectedOptions).forEach((option) => {
                if (option && option.price) {
                    this.price += option.price;
                }
            });

            this.price = this.price * this.quantity;

            if (
                this.lastSelectedSize !== this.selectedOptions.size.id &&
                this.lastSelectedSize !== null &&
                this.isMeasureVisible !== true
            ) {
                this.isMeasureVisible = true;
                setTimeout(() => {
                    this.isMeasureVisible = false;
                }, 3000);
            }

            this.lastSelectedSize = this.selectedOptions.size.id;
        },
        clearOption(option) {
            this.selectedOptions[option].id = null;
        },
        handleSelectedOptionsChange(newOptions) {
            this.checkOptions(newOptions);
        },
        handleQuantityChange() {
            this.checkOptions(this.selectedOptions);
        },
        sliceInputText(text, length) {
            text = text.slice(length);
        },
    },
};
</script>

<style>
/* Keyframes at top level so they are not stripped in production (extractCSS / PostCSS) */
@keyframes fadeInImage {
    from {
        @apply opacity-0 translate-y-2;
    }
    to {
        @apply opacity-100 translate-y-0;
    }
}

@keyframes l20-1 {
    0% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 50% 0, 50% 0, 50% 0%, 50% 0%);
    }
    12.5% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 100% 0, 100% 0, 100% 0%, 100% 0%);
    }
    25% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
    }
    50% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 100% 0, 100% 100%, 50% 100%, 0% 100%);
    }
    62.5% {
        clip-path: polygon(50% 50%, 100% 0, 100% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%);
    }
    75% {
        clip-path: polygon(50% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50% 100%, 0% 100%);
    }
    100% {
        clip-path: polygon(50% 50%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 0% 100%);
    }
}

@keyframes l20-2 {
    0% {
        transform: scaleY(1) rotate(0deg);
    }
    49.99% {
        transform: scaleY(1) rotate(135deg);
    }
    50% {
        transform: scaleY(-1) rotate(0deg);
    }
    100% {
        transform: scaleY(-1) rotate(-135deg);
    }
}

@keyframes slideInFromTop {
    0% {
        @apply -mt-4 opacity-0;
    }
    10% {
        @apply mt-0 opacity-100;
    }
    80% {
        @apply mt-0 opacity-100;
    }
    100% {
        @apply -mt-4 opacity-0;
    }
}

@keyframes slideInFromLeft {
    0% {
        @apply -ml-4 opacity-0;
    }
    10% {
        @apply ml-0 opacity-100;
    }
    80% {
        @apply ml-0 opacity-100;
    }
    100% {
        @apply -ml-4 opacity-0;
    }
}

@keyframes border-dance {
    0% {
        background-position: 0 0, 300px 116px, 0 150px, 216px 0;
    }
    100% {
        background-position: 300px 0, 0 116px, 0 0, 216px 150px;
    }
}

.box-shadow-sm {
    @media (min-width: 992px) {
        box-shadow: 0 4px 60px 0 rgba(21, 33, 55, 0.08);
    }
}

.number-input {
    input {
        @apply p-2;
        border-radius: 0.25rem;
    }
}

.quantity {
    input {
        @apply text-center;
    }
}

.adavantage-list {
    li {
        @apply flex items-center gap-x-2;

        &::before {
            content: '\eb7b';
            @apply w-4 h-4 bg-center bg-contain bg-no-repeat text-lg text-blue-100 flex items-center justify-center;
            font-family: 'remixicon', sans-serif;
        }
    }
}

.cart-btn {
    &:active {
        background-color: #ff9d00;
    }
}

.rating-btn-wrapper {
    transition-delay: 0.15s;
}

.gallery-image {
    box-shadow: 0 5.4511px 109.02208px 0 rgba(0, 0, 0, 0.05);
}

.product-image {
    &.isLoading {
        animation: fadeInImage 0.25s forwards 0.25s;
    }

    &.isLoaded {
        @apply opacity-100 translate-y-0;
    }
}

.loader {
    width: 23px;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 3px solid #001426;
    animation: l20-1 0.8s infinite linear alternate, l20-2 1.6s infinite linear;
}

.engraved-text-to-plastic {
    font-weight: 600;
    color: white;
    &--light {
        color: #ebe4e7;
    }
}

.engraved-text-to-wood {
    color: #360700;
    text-shadow: 1px 1px 2px #3607004d, 0 0 1em #36070040, 0 0 0.2em #36070073;
}

.engraved-text-to-wood-medium {
    color: #392408;
    mix-blend-mode: multiply;
    text-shadow: 0.25px 0.25px 0 rgba(36, 22, 0, 0.5);
}

.engraved-text-to-wood-dark {
    background-color: #392408;
    color: #392408;
    text-shadow: 0.5px 0.5px 0 rgba(36, 22, 0, 0.5);
    -webkit-background-clip: text;
    -moz-background-clip: text;
    background-clip: text;
}

.measure {
    background: linear-gradient(90deg, #001426 50%, transparent 50%),
        linear-gradient(90deg, #001426 50%, transparent 50%), linear-gradient(0deg, #001426 50%, transparent 50%),
        linear-gradient(0deg, #001426 50%, transparent 50%);
    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
    background-size: 8px 2px, 8px 2px, 2px 8px, 2px 8px;
    background-position: 0 0, 212px 116px, 0 116px, 216px 0;
}

.measure--y {
    height: 73%;
    margin-top: 15%;
    left: 3rem;
    width: 1px;
    opacity: 0;
    animation: border-dance 4s infinite linear;
}

@media (min-width: 1024px) {
    .measure--y {
        left: 5.1rem;
    }
}

.measure--y.is-visible {
    animation: slideInFromLeft 3s forwards, border-dance 4s infinite linear;
}

.measure--y-horizontal {
    height: 55.5%;
    margin-top: 22.15%;
    left: 2rem;
}

@media (min-width: 640px) {
    .measure--y-horizontal {
        left: 2.25rem;
    }
}

@media (min-width: 1024px) {
    .measure--y-horizontal {
        left: 3rem;
    }
}

.measure--x {
    height: 1px;
    width: 57%;
    margin-left: 21.5%;
    top: 2rem;
    opacity: 0;
    animation: border-dance 8s infinite linear;
}

@media (min-width: 1024px) {
    .measure--x {
        top: 2.75rem;
    }
}

.measure--x.is-visible {
    animation: slideInFromTop 3s forwards, border-dance 8s infinite linear;
}

.measure--x-horizontal {
    top: 4rem;
    width: 74%;
    margin-left: 14%;
}

@media (min-width: 640px) {
    .measure--x-horizontal {
        top: 5rem;
    }
}

@media (min-width: 1024px) {
    .measure--x-horizontal {
        top: 6.5rem;
        width: 74%;
        margin-left: 14%;
    }
}
</style>
